import { useState, useEffect, useRef, useId } from 'react';
import PropTypes from 'prop-types';

/**
 * Accessible custom grain-variety dropdown.
 *
 * Styling is driven entirely by the parent page's CSS using a class prefix
 * convention. Pass classPrefix matching the CSS namespace in use:
 *   - "catalog__grain"  → catalog page (Catalog.css)
 *   - "pp-grain"        → customer portal page (Products.css)
 *
 * The component generates class names like:
 *   {classPrefix}-dropdown, {classPrefix}-trigger, {classPrefix}-list, etc.
 */
const GrainDropdown = ({ options, value, onChange, classPrefix }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const id = useId();
  const p = classPrefix;

  const selected = options.find((o) => String(o.id) === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div className={`${p}-dropdown`} ref={ref}>
      <button
        type="button"
        className={`${p}-trigger${value ? ` ${p}-trigger--active` : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
      >
        <span className={`${p}-trigger-label`}>Grain</span>
        <span className={`${p}-trigger-value`}>{selected ? selected.name : 'All'}</span>
        <svg
          className={`${p}-chevron${open ? ` ${p}-chevron--open` : ''}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          aria-hidden="true"
        >
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        </svg>
      </button>

      {open && (
        <ul id={id} className={`${p}-list`} role="listbox" aria-label="Select grain variety">
          <li
            role="option"
            aria-selected={!value}
            className={`${p}-option${!value ? ` ${p}-option--selected` : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            All Grains
          </li>
          {options.map((o) => (
            <li
              key={o.id}
              role="option"
              aria-selected={String(o.id) === value}
              className={`${p}-option${String(o.id) === value ? ` ${p}-option--selected` : ''}`}
              onClick={() => { onChange(String(o.id)); setOpen(false); }}
            >
              {o.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

GrainDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  classPrefix: PropTypes.string.isRequired,
};

export default GrainDropdown;
